import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import Navbar from '../../components/HomepageComponents/navbar';
import FooterSection from '../../components/HomepageComponents/footer';

/* ── Glass mixin ── */
const glass = css`
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 14px 45px rgba(0, 0, 0, 0.16);
`;

/* ── Project data ── */
const projects = [
  {
    title: 'LISA — Local Irrigation System Assessment',
    subtitle: 'Irrigation workstream project under the MAKASA Program',
    tags: ['Active', 'Dissertation Track', 'Extension-ready'],
    cta: { label: 'Open LISA', href: 'https://lisa.bisubilar.org/' },
    status: 'active',
  },
  {
    title: 'Soil Analytics (Planned)',
    subtitle: 'Reserved for future program projects (outside dissertation scope).',
    tags: ['Planned'],
    status: 'planned',
  },
  {
    title: 'Pest Advisory (Planned)',
    subtitle: 'Reserved for future program projects (outside dissertation scope).',
    tags: ['Planned'],
    status: 'planned',
  },
  {
    title: 'Market Insights (Planned)',
    subtitle: 'Reserved for future program projects (outside dissertation scope).',
    tags: ['Planned'],
    status: 'planned',
  },
];

/* ── Component ── */
const Projects = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <PageWrap>
        <BackButton onClick={() => navigate('/')}>
          <FaArrowLeft size={14} /> Back to Home
        </BackButton>

        <PageTitle>Projects</PageTitle>
        <PageSubtitle>
          MAKASA is the umbrella program. Each card represents a workstream project — only LISA is currently active.
        </PageSubtitle>

        <Grid>
          {projects.map((p, i) => (
            <Card key={i} $active={p.status === 'active'}>
              <TagRow>
                {p.tags.map((t, j) => (
                  <Tag key={j} $type={t}>{t}</Tag>
                ))}
              </TagRow>
              <CardTitle>{p.title}</CardTitle>
              <CardSubtitle>{p.subtitle}</CardSubtitle>
              {p.cta && (
                <CtaButton
                  href={p.cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.cta.label} <FaExternalLinkAlt size={12} />
                </CtaButton>
              )}
            </Card>
          ))}
        </Grid>
      </PageWrap>
      <FooterSection />
    </>
  );
};

export default Projects;

/* ── Styled Components ── */

const PageWrap = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 7rem 1.5rem 4rem;
  min-height: 100vh;
`;

const BackButton = styled.button`
  background: #3d6b1f;
  color: #f4f1e8;
  padding: 0.6rem 1.4rem;
  border-radius: 25px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2rem;
  border: none;
  box-shadow: 0 4px 10px rgba(61, 107, 31, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: #2d5016;
    transform: translateY(-2px);
  }
`;

const PageTitle = styled.h1`
  font-family: 'Chau Philomene One', sans-serif;
  font-size: 2.4rem;
  color: #2d5016;
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #5a6b52;
  margin-bottom: 2.5rem;
  max-width: 680px;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  ${glass}
  border-radius: 18px;
  padding: 1.75rem;
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, rgba(19,111,7,0.12), rgba(255,255,255,0.12))'
      : 'rgba(255,255,255,0.08)'};
  border: 2px solid ${({ $active }) => ($active ? '#3d6b1f' : '#e8e4d8')};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(61, 107, 31, 0.18);
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const tagColors = {
  Active: { bg: '#e8f5e9', color: '#2d5016', border: '#3d6b1f' },
  'Dissertation Track': { bg: '#fff8e1', color: '#8d6e00', border: '#b8860b' },
  'Extension-ready': { bg: '#e3f2fd', color: '#1a5276', border: '#2980b9' },
  Planned: { bg: '#f4f1e8', color: '#777', border: '#ccc' },
};

const Tag = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.25rem 0.7rem;
  border-radius: 20px;
  background: ${({ $type }) => tagColors[$type]?.bg || '#f4f1e8'};
  color: ${({ $type }) => tagColors[$type]?.color || '#555'};
  border: 1.5px solid ${({ $type }) => tagColors[$type]?.border || '#ccc'};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CardTitle = styled.h3`
  font-family: 'Chau Philomene One', sans-serif;
  font-size: 1.2rem;
  color: #2d5016;
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 0.92rem;
  color: #3a3434;
  line-height: 1.55;
  margin: 0;
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  padding: 0.6rem 1.4rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-decoration: none;
  background: #3d6b1f;
  color: #fff;
  border: none;
  width: fit-content;
  box-shadow: 0 4px 10px rgba(61, 107, 31, 0.2);
  transition: all 0.25s ease;

  &:hover {
    background: #2d5016;
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(61, 107, 31, 0.35);
    color: #fff;
  }
`;
